import React, { useEffect, useRef, useState } from "react";

// -----------------------------------------------------------------------
// TeleprompterRecorder (2026-08-28)
//
// A modal-style browser recorder that displays the 45-second script
// scrolling at reading speed above a live webcam preview, then uploads
// the .webm to Object Storage via the parent's onUpload callback.
// -----------------------------------------------------------------------

const SUPPORTED_MIME_TYPES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4",
];

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  for (const mt of SUPPORTED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(mt)) {
      return mt;
    }
  }
  return "";
}

function fmtTime(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(1, "0")}:${String(r).padStart(2, "0")}`;
}

export default function TeleprompterRecorder({
  productKey,
  productDisplay,
  script,
  onClose,
  onUpload,
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const promptRef = useRef(null);
  const scrollTimerRef = useRef(null);
  const durationTimerRef = useRef(null);
  const startedAtRef = useRef(0);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState(null); // { blob, url, mime, size }
  const [uploading, setUploading] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState(0.5); // px per tick (~30ms)

  // Boot: request camera + mic.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;
          videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        setError(
          e?.message ||
            "Could not access your camera and microphone. Check permissions and try again.",
        );
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (scrollTimerRef.current) clearInterval(scrollTimerRef.current);
      if (durationTimerRef.current) clearInterval(durationTimerRef.current);
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecording = () => {
    if (!streamRef.current) return;
    setError(null);
    chunksRef.current = [];
    const mime = pickMimeType();
    let recorder;
    try {
      recorder = mime
        ? new MediaRecorder(streamRef.current, { mimeType: mime })
        : new MediaRecorder(streamRef.current);
    } catch (e) {
      setError("This browser does not support in-page recording. Try Chrome or Edge.");
      return;
    }
    recorder.ondataavailable = (evt) => {
      if (evt.data && evt.data.size > 0) chunksRef.current.push(evt.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, {
        type: mime || "video/webm",
      });
      const url = URL.createObjectURL(blob);
      setPreview({
        blob,
        url,
        mime: mime || "video/webm",
        size: blob.size,
        durationSeconds: (Date.now() - startedAtRef.current) / 1000,
      });
      setRecording(false);
      if (scrollTimerRef.current) {
        clearInterval(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
    };
    recorderRef.current = recorder;
    startedAtRef.current = Date.now();
    setElapsed(0);
    recorder.start(500);
    setRecording(true);
    // Auto-scroll teleprompter
    if (promptRef.current) promptRef.current.scrollTop = 0;
    scrollTimerRef.current = setInterval(() => {
      if (promptRef.current) promptRef.current.scrollTop += scrollSpeed;
    }, 30);
    durationTimerRef.current = setInterval(() => {
      setElapsed((Date.now() - startedAtRef.current) / 1000);
    }, 250);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const resetTake = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setElapsed(0);
  };

  const doUpload = async () => {
    if (!preview) return;
    setUploading(true);
    try {
      await onUpload({
        blob: preview.blob,
        mime: preview.mime,
        durationSeconds: preview.durationSeconds,
      });
    } catch (e) {
      setError(e?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="teleprompter-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20, 22, 28, 0.75)",
        zIndex: 90,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "min(1000px, 100%)",
          maxHeight: "94vh",
          overflow: "auto",
          background: "var(--bo-panel, #fff)",
          borderRadius: 14,
          border: "1px solid var(--bo-border, #e6e2d8)",
          padding: 20,
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div>
            <div className="bo-eyebrow">Record &middot; {productKey}</div>
            <h2
              style={{
                margin: "4px 0 0 0",
                fontFamily: "var(--bo-serif)",
                fontSize: 22,
              }}
              data-testid="teleprompter-title"
            >
              {productDisplay} &mdash; 45-second take
            </h2>
          </div>
          <button
            className="bo-btn bo-btn--ghost"
            onClick={onClose}
            data-testid="teleprompter-close"
            disabled={uploading}
            style={{ padding: "8px 14px" }}
          >
            Close
          </button>
        </div>

        {error && (
          <div
            role="alert"
            data-testid="teleprompter-error"
            style={{
              background: "#fee2d2",
              color: "#8a2b0a",
              padding: 10,
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          {/* Teleprompter */}
          <div
            ref={promptRef}
            data-testid="teleprompter-prompt"
            style={{
              background: "#101317",
              color: "#f4e9c8",
              borderRadius: 10,
              padding: 24,
              fontSize: 26,
              lineHeight: 1.5,
              fontFamily: "var(--bo-serif)",
              height: 360,
              overflow: "hidden",
              whiteSpace: "pre-wrap",
              boxShadow: "inset 0 0 40px rgba(0,0,0,0.4)",
            }}
          >
            {script || "(no script yet — go back and generate one first)"}
            <div style={{ height: 200 }} aria-hidden />
          </div>

          {/* Camera preview / playback */}
          <div
            style={{
              background: "#000",
              borderRadius: 10,
              overflow: "hidden",
              height: 360,
              position: "relative",
            }}
          >
            {!preview ? (
              <video
                ref={videoRef}
                data-testid="teleprompter-live-preview"
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transform: "scaleX(-1)", // mirror so it feels like a mirror
                }}
              />
            ) : (
              <video
                data-testid="teleprompter-recorded-preview"
                src={preview.url}
                controls
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  background: "#000",
                }}
              />
            )}
            {recording && (
              <div
                data-testid="teleprompter-rec-indicator"
                style={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  padding: "4px 10px",
                  borderRadius: 999,
                  background: "rgba(200, 30, 30, 0.9)",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                }}
              >
                &#9679; REC {fmtTime(elapsed)}
              </div>
            )}
            {!recording && !preview && ready && (
              <div
                style={{
                  position: "absolute",
                  bottom: 10,
                  left: 10,
                  color: "#f4e9c8",
                  fontSize: 12,
                  padding: "3px 8px",
                  background: "rgba(0,0,0,0.5)",
                  borderRadius: 4,
                }}
              >
                Preview
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div
          style={{
            display: "flex",
            gap: 10,
            marginTop: 14,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <label
            style={{
              display: "inline-flex",
              gap: 8,
              alignItems: "center",
              fontSize: 13,
              color: "var(--bo-muted)",
            }}
          >
            Scroll speed
            <input
              type="range"
              min={0.1}
              max={2}
              step={0.1}
              value={scrollSpeed}
              onChange={(e) => setScrollSpeed(parseFloat(e.target.value))}
              data-testid="teleprompter-scroll-speed"
            />
          </label>

          {!recording && !preview && (
            <button
              className="bo-btn bo-btn--primary"
              onClick={startRecording}
              disabled={!ready}
              data-testid="teleprompter-start"
              style={{ padding: "10px 18px" }}
            >
              &#9679; Start recording
            </button>
          )}
          {recording && (
            <button
              className="bo-btn bo-btn--primary"
              onClick={stopRecording}
              data-testid="teleprompter-stop"
              style={{ padding: "10px 18px", background: "#b83232" }}
            >
              &#9632; Stop
            </button>
          )}
          {preview && (
            <>
              <button
                className="bo-btn bo-btn--ghost"
                onClick={resetTake}
                disabled={uploading}
                data-testid="teleprompter-retake"
                style={{ padding: "10px 18px" }}
              >
                Retake
              </button>
              <button
                className="bo-btn bo-btn--primary"
                onClick={doUpload}
                disabled={uploading}
                data-testid="teleprompter-upload"
                style={{ padding: "10px 18px" }}
              >
                {uploading ? "Uploading\u2026" : "Save & publish"}
              </button>
              <div style={{ color: "var(--bo-muted)", fontSize: 12 }}>
                {(preview.size / 1024 / 1024).toFixed(2)} MB &middot;{" "}
                {fmtTime(preview.durationSeconds || 0)}
              </div>
            </>
          )}
        </div>

        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: "var(--bo-muted)",
          }}
        >
          Target: ~45 seconds. Files are uploaded to secure object storage &mdash; nothing is stored on the app pod.
        </div>
      </div>
    </div>
  );
}
